import React from 'react';

import { withStyles } from '@material-ui/core/styles';
import MuiIconButton from '@material-ui/core/IconButton';
import MuiEditAttributesIcon from '@material-ui/icons/EditAttributes';
import MuiDoneIcon from '@material-ui/icons/Done';
import MuiSaveIcon from '@material-ui/icons/Save';
import MuiBlockIcon from '@material-ui/icons/Block';
import MuiCircularProgress from '@material-ui/core/CircularProgress';
import MuiErrorIcon from '@material-ui/icons/Error';
import MuiGetAppIcon from '@material-ui/icons/GetApp';
import MuiArchiveIcon from '@material-ui/icons/Archive';
import MuiUnarchiveIcon from '@material-ui/icons/Unarchive';

import { WithTooltip } from 'utils/wrappers';
import { API_STATUS_KEYS, ERRORS } from 'utils/constants';

const styles = () => ({
  hiddenButton: {
    visibility: 'hidden',
    width: 40,
  },
});

const QUERY_STATUS = {
  LOADING: 'loading',
  ERROR: 'error',
};

const RowApproveButtons = ({
  classes,
  row,
  formData,
  isArchived,
  editing,
  onEditChange,
  onApproveInvoice,
  onArchiveInvoice,
  onUnarchiveInvoice,
  onExportClick,
  onSaveIncomingPayments,
  patchStatus,
  approveStatus,
  exportStatus,
  archiveStatus,
}) => {
  const isNotArchived = !row.is_archived;
  const isApproved = row.is_approved === true;

  const disableExport = false;

  const canPatch =
    !isNaN(parseFloat(formData.incoming_payment_native)) &&
    !isNaN(parseFloat(formData.incoming_payment_usd)) &&
    (formData.incoming_payment_native !== row.incoming_payment_native ||
      formData.incoming_payment_usd !== row.incoming_payment_usd) &&
    isNotArchived;

  const canApprove =
    row.is_approved === false &&
    !isNaN(parseFloat(row.incoming_payment_native)) &&
    !isNaN(parseFloat(row.incoming_payment_usd)) &&
    formData.incoming_payment_native === row.incoming_payment_native &&
    formData.incoming_payment_usd === row.incoming_payment_usd &&
    isNotArchived;

  // Listeners
  const handleEditClick = () => onEditChange(true);
  const handleCancelClick = () => onEditChange(false);
  const handleExportClick = onExportClick;

  // Components

  const ActionSave = (
    <WithTooltip tooltip="Save Changes" disabled={!canPatch}>
      <MuiIconButton size="small" className={classes.buttonSave} onClick={onSaveIncomingPayments}>
        <MuiSaveIcon fontSize="small" />
      </MuiIconButton>
    </WithTooltip>
  );

  const ActionErrorSave = (
    <WithTooltip tooltip={ERRORS.GENERAL.TRY_AGAIN} disabled={!canPatch}>
      <MuiIconButton size="small" type="submit">
        <MuiErrorIcon fontSize="small" />
      </MuiIconButton>
    </WithTooltip>
  );

  const ActionApprove = (
    <WithTooltip tooltip="Approve Invoice" disabled={!canApprove}>
      <MuiIconButton size="small" className={classes.buttonDone} disabled={!canApprove} onClick={onApproveInvoice}>
        <MuiDoneIcon fontSize="small" />
      </MuiIconButton>
    </WithTooltip>
  );

  const ActionArchive = (
    <WithTooltip tooltip="Archive" disabled={isApproved}>
      <MuiIconButton size="small" disabled={isApproved} onClick={onArchiveInvoice}>
        <MuiArchiveIcon fontSize="small" />
      </MuiIconButton>
    </WithTooltip>
  );

  const ActionUnarchive = (
    <WithTooltip tooltip="Unarchive" disabled={isApproved}>
      <MuiIconButton size="small" disabled={isApproved} onClick={onUnarchiveInvoice}>
        <MuiUnarchiveIcon fontSize="small" />
      </MuiIconButton>
    </WithTooltip>
  );

  const ActionErrorApprove = (
    <WithTooltip tooltip={ERRORS.GENERAL.TRY_AGAIN} disabled={!canApprove}>
      <MuiIconButton size="small" disabled={!canApprove} onClick={onApproveInvoice}>
        <MuiErrorIcon fontSize="small" />
      </MuiIconButton>
    </WithTooltip>
  );

  const ActionExport = (
    <WithTooltip tooltip="Export CSV" disabledTooltip="Must Have Royalties" disabled={disableExport}>
      <MuiIconButton size="small" className={classes.buttonCancel} onClick={handleExportClick} disabled={disableExport}>
        <MuiGetAppIcon fontSize="small" />
      </MuiIconButton>
    </WithTooltip>
  );

  const ActionExportError = (
    <WithTooltip disabled={disableExport}>
      <MuiIconButton size="small" disabled={disableExport} onClick={handleExportClick}>
        <MuiErrorIcon fontSize="small" />
      </MuiIconButton>
    </WithTooltip>
  );

  const ActionCancel = (
    <WithTooltip tooltip="Cancel">
      <MuiIconButton size="small" className={classes.buttonCancel} onClick={handleCancelClick}>
        <MuiBlockIcon fontSize="small" />
      </MuiIconButton>
    </WithTooltip>
  );

  const ActionInProgress = (
    <MuiIconButton size="small" disabled={true}>
      <MuiCircularProgress size={'0.8rem'} />
    </MuiIconButton>
  );

  const ActionSuccess = (
    <MuiIconButton size="small" className={classes.buttonDone} disabled={true}>
      <MuiDoneIcon fontSize="small" />
    </MuiIconButton>
  );

  return (
    <>
      {!editing ? (
        <>
          <WithTooltip tooltip="Reveal Row Actions" key="edit">
            <MuiIconButton size="small" onClick={handleEditClick}>
              <MuiEditAttributesIcon fontSize="small" />
            </MuiIconButton>
          </WithTooltip>
        </>
      ) : (
        <>
          {patchStatus === QUERY_STATUS.LOADING
            ? ActionInProgress
            : patchStatus === QUERY_STATUS.ERROR
            ? ActionErrorSave
            : ActionSave}
          {approveStatus === QUERY_STATUS.LOADING
            ? ActionInProgress
            : approveStatus === QUERY_STATUS.ERROR
            ? ActionErrorApprove
            : approveStatus === API_STATUS_KEYS.COMPLETE
            ? ActionSuccess
            : ActionApprove}
          {exportStatus === QUERY_STATUS.LOADING
            ? ActionInProgress
            : exportStatus === QUERY_STATUS.ERROR
            ? ActionExportError
            : ActionExport}
          {archiveStatus == QUERY_STATUS.LOADING ? ActionInProgress : isArchived ? ActionUnarchive : ActionArchive}
          {ActionCancel}
        </>
      )}
    </>
  );
};

export default withStyles(styles)(RowApproveButtons);
